import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Mail, Phone, Building2 } from "lucide-react";

const empty = { nom: "", prenom: "", societe: "", phone: "", email: "", password: "" };

export default function AdminClients() {
  const { t } = useLang();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...empty }); };
  const openEdit = (c) => { setEditing(c); setForm({ nom: c.nom, prenom: c.prenom, societe: c.societe, phone: c.phone, email: c.email, password: "" }); };

  const save = async () => {
    try {
      if (editing) { await api.put(`/clients/${editing.id}`, form); toast.success(t("clients.updated")); }
      else { await api.post("/clients", form); toast.success(t("clients.created")); }
      setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(t("clients.del_confirm"))) return;
    await api.delete(`/clients/${c.id}`);
    toast.success(t("clients.deleted")); load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="ch-fade-up space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("clients.eyebrow")}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("nav.clients")}</h1>
        </div>
        <Button data-testid="new-client-button" onClick={openNew} className="rounded-sm bg-black hover:bg-neutral-800">
          <Plus className="mr-2 h-4 w-4" /> {t("clients.new")}
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {clients.map((c) => (
          <div key={c.id} data-testid={`client-card-${c.id}`} className="group rounded-sm border border-neutral-200 p-5 transition-colors hover:border-black">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-bold">{c.prenom} {c.nom}</h3>
                {c.societe && <p className="flex items-center gap-1.5 text-sm text-neutral-500"><Building2 className="h-3.5 w-3.5" />{c.societe}</p>}
              </div>
              <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="rounded-sm p-1.5 hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-client-${c.id}`} onClick={() => del(c)} className="rounded-sm p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-neutral-600">
              <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-neutral-400" />{c.email}</p>
              {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-neutral-400" />{c.phone}</p>}
            </div>
            <div className="mt-4 border-t border-neutral-100 pt-3 text-xs text-neutral-400">
              {c.orders_count} {c.orders_count > 1 ? t("common.orders") : t("common.order")}
            </div>
          </div>
        ))}
        {clients.length === 0 && <p className="text-sm text-neutral-400">{t("clients.empty")}</p>}
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent data-testid="client-form-dialog" className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? t("clients.edit") : t("clients.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{t("clients.firstname")}</Label><Input data-testid="client-prenom" value={form.prenom} onChange={set("prenom")} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>{t("clients.lastname")}</Label><Input data-testid="client-nom" value={form.nom} onChange={set("nom")} className="rounded-sm" /></div>
              <div className="col-span-2 space-y-1.5"><Label>{t("clients.company")}</Label><Input data-testid="client-societe" value={form.societe} onChange={set("societe")} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>{t("clients.phone")}</Label><Input data-testid="client-phone" value={form.phone} onChange={set("phone")} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>{t("common.email")}</Label><Input data-testid="client-email" type="email" value={form.email} onChange={set("email")} className="rounded-sm" /></div>
              <div className="col-span-2 space-y-1.5">
                <Label>{t("common.password")} {editing && <span className="text-neutral-400">{t("clients.pw_keep")}</span>}</Label>
                <Input data-testid="client-password" type="text" value={form.password} onChange={set("password")} className="rounded-sm" placeholder={editing ? "••••••••" : t("clients.pw_ph")} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button data-testid="save-client-button" onClick={save} className="rounded-sm bg-black hover:bg-neutral-800">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
